import React, { useRef } from "react";
import artificialIntelligence from "../../assets/caseStudies/artificial-intelligence.png";
import biosimilar from "../../assets/caseStudies/biosimilar-prescription.png";
import concrete from "../../assets/caseStudies/concrete-equipment.png";
import eyeTreatment from "../../assets/caseStudies/eye-treatment.png";
import highwayBattery from "../../assets/caseStudies/highway-battery-pack.png";
import mobilePayments from "../../assets/caseStudies/mobile-payments.png";
import { AnimatePresence, motion } from "framer-motion";

function CaseStudies() {
  const caseStudyRef = React.useRef(null);
  const caseStudies = [
    {
      title: "Artificial Intelligence Strategy",
      problem:
        "A premier management consulting firm approached Expertefy to gain a deeper understanding of artificial intelligence strategy. Their goal was to unravel AI trends, discern the usage of emergent technologies, and identify the principal challenges currently shaping the AI landscape.",
      solution:
        "The Expertefy research team promptly embarked on the project, securing interviews with leading industry experts. Through a meticulous screening process, they ensured that each expert was closely aligned with the topic at hand. The client was presented with a curated selection of experts, from which they chose six for comprehensive interviews. Expertefy provided the client with both audio recordings and accurate AI-generated transcripts post-interview, facilitating an in-depth understanding of AI's strategic applications and its challenges.",
      imageSource: artificialIntelligence,
    },
    {
      title: "Mobile Payment Trends in the Middle East",
      problem:
        "A private equity firm required expertise to navigate the mobile payment sector in the Middle East, looking to understand the nuances of the faster payment value chain, including key players and trends.",
      solution:
        "Expertefy custom recruited 6 experts from the region's financial sector, conducted thorough interviews, and gathered specialists including Payment Directors and Financial Heads. They provided valuable trends in online payments, insights into the value chain of faster payments, and how mobile payment methods are being leveraged by businesses.",
      imageSource: mobilePayments,
    },
    {
      title: "Off-Highway Battery Packs",
      problem:
        "A leading European strategy consulting firm was keen to delve into the market for off-highway battery packs, focusing on Material Handling, Specialty Vehicles, and Electrified Truck Trailers. They were particularly interested in the regulatory factors propelling growth in material handling and specialty vehicles. Furthermore, they sought insights on corporate responsibility within this sector and projected growth for electrified airport and port vehicles up to 2030.",
      solution:
        "Expertefy conducted thorough research to curate a list of OEMs active in Material Handling and Electrified Truck Trailers. From this list, a diverse group of industry leaders and technical experts, including those specializing in battery technology, R&D, and engineering, were identified and consulted. Despite tight deadlines, Expertefy efficiently completed all interviews within two weeks. The resulting insights allowed the consulting firm to grasp the regulatory landscape, growth potential, and key success drivers in this evolving market.",
      imageSource: highwayBattery,
    },
    {
      title: "Innovative Eye Treatment Technologies",
      problem:
        "An APAC healthcare consultancy was investigating medical devices for genetic eye testing. They aimed to gather detailed information on the device's potential to address unmet needs relating to corneal dystrophy, glaucoma, LASIK surgery, and genetic risks of keratoconus. They sought the expertise of medical professionals to understand the diagnostic landscape, cost structures, and regulatory pathways in Vietnam, Malaysia, Taiwan, Hong Kong, and Australia.",
      solution:
        "Expertefy identified and recruited 18 Key Opinion Leaders (KOLs) from ophthalmology, focusing on glaucoma and refractive corneal treatment, as well as pathologists and medical device regulatory experts. This bespoke recruitment was tailored to the highly specific subject matter and targeted regions. Twelve experts were interviewed, and upon request, an interview guide was provided beforehand to ensure preparedness. Consequently, the consultancy achieved a comprehensive understanding of the regulatory environment across the designated APAC regions.",
      imageSource: eyeTreatment,
    },
    {
      title: "Biosimilars Prescription and Treatment Landscapes",
      problem:
        "A global consultancy firm required assistance in interviewing Payers and Physicians to gain insights into the prescription and treatment practices of biosimilars within healthcare settings. They were interested in perspectives from Payers, Hematologists, Pharmacy Directors, and Medical Directors.",
      solution:
        "Expertefy swiftly sourced and rigorously vetted 18 experts in the USA, including Payers, Hematologists, Pharmacy Directors, and Medical Directors, aligning with the client's specifications. All experts received preparatory materials before the interviews. The insights obtained enabled the client to comprehend the complexities that healthcare centers face when prescribing Biosimilars versus alternative treatments.",
      imageSource: biosimilar,
    },
    {
      title: "Concrete Equipment Market Analysis",
      problem:
        "A management consulting firm and its client were evaluating the concrete equipment market for potential investment opportunities. They sought to understand market dynamics, sales channels, and recent shifts in the industry, all within a tight two-week timeframe. Their focus was on speaking with recently departed manufacturing insiders from the concrete market.",
      solution:
        "Expertefy recommended a selection of former experts from key manufacturing companies, including profiles like Construction Equipment Directors, Regional Managers, and Senior Sales Professionals from various regions. Through MS Teams interviews conducted by the client and supplemented by audio recordings and transcripts provided by Expertefy, the client gained valuable insights into current market trends, sales strategies, and future forecasts of the concrete equipment market, including products like compactors, power buggies, blades, and wheels.",
      imageSource: concrete,
    },
  ];
  const [selectedCaseStudy, setSelectedCaseStudy] = React.useState(null);

  const handleSelectCaseStudy = (caseStudy) => {
    setSelectedCaseStudy(caseStudy);
    setTimeout(() => {
      caseStudyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100); // Optional delay to wait for state change
  };

  const variants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, type: "spring" } },
    exit: { opacity: 0, scale: 0.5, transition: { type: "spring", duration: 0.5 } },
  };

  return (
    <>
      {selectedCaseStudy &&  (
        <AnimatePresence>
          <motion.section 
            ref={caseStudyRef}
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="flex justify-center items-center py-8 w-full max-md:px-5 max-md:mt-5 max-md:max-w-full drop-shadow-2xl">
            <div className="flex flex-col w-full max-w-[1120px] max-md:max-w-full">
              <div className="flex flex-col flex-wrap justify-center content-start px-2.5 py-3 bg-dark-indigo rounded-xl shadow-xl">
                <div className="self-center text-xl sm:text-3xl font-medium text-white max-md:max-w-full">
                  {selectedCaseStudy.title}
                </div>
                <img
                  loading="lazy"
                  srcSet={selectedCaseStudy.imageSource}
                  className="mt-2 w-full aspect-[3.7] max-md:max-w-full"
                />
                <div className="mt-2 max-md:max-w-full">
                  <div className="flex gap-5 max-md:flex-col max-md:gap-0">
                    <div className="flex flex-col w-6/12 max-md:ml-0 max-md:w-full">
                      <div className="text-lg text-white capitalize max-md:mt-2 max-md:max-w-full">
                        <span className="text-xl font-medium capitalize">
                          The Problem:
                        </span>
                        <br />
                        {selectedCaseStudy.problem}
                      </div>
                    </div>
                    <div className="flex flex-col ml-5 w-6/12 max-md:ml-0 max-md:w-full">
                      <div className="text-lg text-white capitalize max-md:mt-2 max-md:max-w-full">
                        <span className="text-xl font-medium capitalize">
                          Expertefy Solution:
                        </span>
                        <br />
                        {selectedCaseStudy.solution}
                        <br />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        </AnimatePresence>
      )}

      <section className="flex justify-center items-center py-8 w-full max-md:px-5 max-md:mt-5 max-md:max-w-full">
        <div className="flex flex-col w-full max-w-[1120px] max-md:max-w-full">
          <div className="self-center text-2xl sm:text-5xl font-medium leading-10 text-center light-title capitalize">
            Case Studies
          </div>
          <div className="self-center mt-6 text-lg text-center dark-title max-md:max-w-full">
            The team at Expertefy is committed to offering personalized services
            that meet the specific needs of our clients, focusing on sourcing
            the most relevant and hard-to-find experts that fit.
          </div>
          <div className="mt-6 w-full max-md:max-w-full">
            <div className="flex gap-5 max-md:flex-col max-md:gap-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1}}
                transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
                viewport={{ once: true }} 
                className="flex flex-col w-6/12 max-md:ml-0 max-md:w-full drop-shadow-2xl"
                onClick={() => handleSelectCaseStudy(caseStudies[0])}
              >
                <div className="grow justify-center p-3 w-full bg-dark-indigo rounded-xl shadow-xl max-md:mt-8 max-md:max-w-full">
                  <div className="flex gap-5 max-md:flex-col max-md:gap-0">
                    <div className="flex flex-col w-[46%] max-md:ml-0 max-md:w-full">
                      <div className="self-stretch my-auto text-xl font-medium text-white max-md:mt-10">
                        Artificial Intelligence Strategy
                      </div>
                    </div>
                    <div className="flex flex-col ml-5 w-[54%] max-md:ml-0 max-md:w-full">
                      <img
                        loading="lazy"
                        srcSet={artificialIntelligence}
                        className="grow w-full h-full object-cover aspect-[2] max-md:mt-6"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1}}
                transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
                viewport={{ once: true }} 
                className="flex flex-col ml-5 w-6/12 max-md:ml-0 max-md:w-full drop-shadow-2xl"
                onClick={() => handleSelectCaseStudy(caseStudies[1])}
              >
                <div className="grow justify-center p-3 w-full bg-dark-indigo rounded-xl shadow-xl max-md:mt-8 max-md:max-w-full">
                  <div className="flex gap-5 max-md:flex-col max-md:gap-0">
                    <div className="flex flex-col w-[46%] max-md:ml-0 max-md:w-full">
                      <div className="self-stretch my-auto text-xl font-medium text-white max-md:mt-10">
                        Mobile Payment Trends in the Middle East
                      </div>
                    </div>
                    <div className="flex flex-col ml-5 w-[54%] max-md:ml-0 max-md:w-full">
                      <img
                        loading="lazy"
                        srcSet={mobilePayments}
                        className="grow w-full h-full object-cover aspect-[2] max-md:mt-6"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          <div className="mt-6 w-full max-md:max-w-full">
            <div className="flex gap-5 max-md:flex-col max-md:gap-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1}}
                transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
                viewport={{ once: true }} 
                className="flex flex-col w-6/12 max-md:ml-0 max-md:w-full drop-shadow-2xl"
                onClick={() => handleSelectCaseStudy(caseStudies[2])}
              >
                <div className="grow justify-center p-3 w-full bg-dark-indigo rounded-xl shadow-xl max-md:mt-8 max-md:max-w-full">
                  <div className="flex gap-5 max-md:flex-col max-md:gap-0">
                    <div className="flex flex-col w-[46%] max-md:ml-0 max-md:w-full">
                      <div className="self-stretch my-auto text-xl font-medium text-white max-md:mt-10">
                        Off-Highway Battery Packs
                      </div>
                    </div>
                    <div className="flex flex-col ml-5 w-[54%] max-md:ml-0 max-md:w-full">
                      <img
                        loading="lazy"
                        srcSet={highwayBattery}
                        className="grow w-full h-full object-cover aspect-[2] max-md:mt-6"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1}}
                transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
                viewport={{ once: true }} 
                className="flex flex-col ml-5 w-6/12 max-md:ml-0 max-md:w-full drop-shadow-2xl"
                onClick={() => handleSelectCaseStudy(caseStudies[3])}
              >
                <div className="grow justify-center p-3 w-full bg-dark-indigo rounded-xl shadow-xl max-md:mt-8 max-md:max-w-full">
                  <div className="flex gap-5 max-md:flex-col max-md:gap-0">
                    <div className="flex flex-col w-[46%] max-md:ml-0 max-md:w-full">
                      <div className="self-stretch my-auto text-xl font-medium text-white max-md:mt-10">
                        Innovative Eye Treatment Technologies
                      </div>
                    </div>
                    <div className="flex flex-col ml-5 w-[54%] max-md:ml-0 max-md:w-full">
                      <img
                        loading="lazy"
                        srcSet={eyeTreatment}
                        className="grow w-full h-full object-cover aspect-[2] max-md:mt-6"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          <div className="mt-6 w-full max-md:max-w-full">
            <div className="flex gap-5 max-md:flex-col max-md:gap-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1}}
                transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
                viewport={{ once: true }} 
                className="flex flex-col w-6/12 max-md:ml-0 max-md:w-full drop-shadow-2xl"
                onClick={() => handleSelectCaseStudy(caseStudies[4])}
              >
                <div className="grow justify-center p-3 w-full bg-dark-indigo rounded-xl shadow-xl max-md:mt-8 max-md:max-w-full">
                  <div className="flex gap-5 max-md:flex-col max-md:gap-0">
                    <div className="flex flex-col w-[46%] max-md:ml-0 max-md:w-full">
                      <div className="self-stretch my-auto text-xl font-medium text-white max-md:mt-10">
                        Biosimilars Prescription and Treatment Landscapes
                      </div>
                    </div>
                    <div className="flex flex-col ml-5 w-[54%] max-md:ml-0 max-md:w-full">
                      <img
                        loading="lazy"
                        srcSet={biosimilar}
                        className="grow w-full h-full object-cover aspect-[2] max-md:mt-6"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1}}
                transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
                viewport={{ once: true }} 
                className="flex flex-col ml-5 w-6/12 max-md:ml-0 max-md:w-full drop-shadow-2xl"
                onClick={() => handleSelectCaseStudy(caseStudies[5])}
              >
                <div className="grow justify-center p-3 w-full bg-dark-indigo rounded-xl shadow-xl max-md:mt-8 max-md:max-w-full">
                  <div className="flex gap-5 max-md:flex-col max-md:gap-0">
                    <div className="flex flex-col w-[46%] max-md:ml-0 max-md:w-full">
                      <div className="self-stretch my-auto text-xl font-medium text-white max-md:mt-10">
                        Concrete Equipment Market Analysis
                      </div>
                    </div>
                    <div className="flex flex-col ml-5 w-[54%] max-md:ml-0 max-md:w-full">
                      <img
                        loading="lazy"
                        srcSet={concrete}
                        className="grow w-full h-full object-cover aspect-[2] max-md:mt-6"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default CaseStudies;
